import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    createSectionTaskQuery,
    deleteSectionTaskQuery,
    getSectionTasksQuery,
    setTaskArchivedQuery,
    updateSectionTaskQuery,
} from '../services/taskService';
import { ArchivedTask, BoardTask } from '../types/task';
import { queryKeys } from './queryKeys';
import {
    addToBoardSection,
    addToList,
    BoardDoc,
    changeInList,
    moveBetweenBoardSections,
    removeFromBoardSection,
    removeFromList,
} from './taskTransforms';

// The one place that knows how task data is read, changed and kept consistent.
//
//   Reading: every view draws from a single list of the board's tasks (queryKeys.tasks). A single task is
//   picked out of that list, so there is no second copy of it to fall out of step.
//
//   Changing: each action below is one mutation that updates that list (and, where it applies, the board's
//   per-section ordering) and refreshes whatever is derived from tasks (statistics, search results, the
//   archive). A screen that changes a task calls the matching hook and nothing else; it never edits the cache
//   itself, so it can't forget a view.

const LIST_STALE_MS = 30_000;

// --- reading ----------------------------------------------------------------------------------------------------

export const boardTasksOptions = (boardId: string) => ({
    queryKey: queryKeys.tasks(boardId),
    queryFn: () => getSectionTasksQuery(boardId) as Promise<BoardTask[]>,
    staleTime: LIST_STALE_MS,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
});

// Everything on the board that isn't archived. `refresh` makes it re-read on a timer (for a display that stays open).
export function useBoardTasks(boardId: string, refresh?: { refetchInterval: number }) {
    return useQuery({
        ...boardTasksOptions(boardId),
        ...(refresh ? { refetchInterval: refresh.refetchInterval, refetchIntervalInBackground: true } : {}),
    });
}

// One task, taken from the board's list. Undefined while the list loads or if the task isn't in it.
export function useTask(boardId: string, taskId: string) {
    return useQuery({
        ...boardTasksOptions(boardId),
        select: (tasks: BoardTask[]) => tasks.find((task) => task.id === taskId),
    });
}

// --- changing ---------------------------------------------------------------------------------------------------
// Each *Mutation function returns React Query options, so the cache behaviour can be tested without React; the
// matching use* hook is just `useMutation` around it.

export type NewTask = { sectionId: string, title: string, description?: string, date?: string };

export const createTaskMutation = (queryClient: QueryClient, boardId: string) => ({
    mutationFn: async (input: NewTask): Promise<BoardTask> => {
        const description = input.description ?? '';
        const id = await createSectionTaskQuery(boardId, input.sectionId, input.title, description, input.date);
        if (!id) throw new Error('The server did not return an id for the new task');

        return { id, boardId, sectionId: input.sectionId, title: input.title, description, ...(input.date ? { date: input.date } : {}) };
    },
    onSuccess: (task: BoardTask) => {
        queryClient.setQueryData<BoardTask[]>(queryKeys.tasks(boardId), (tasks) => tasks && addToList(tasks, task));
        queryClient.setQueryData<BoardDoc>(queryKeys.board(boardId), (board) => addToBoardSection(board, task.sectionId, task.id));
        // A new task changes what has come due.
        queryClient.invalidateQueries({ queryKey: queryKeys.boardStats(boardId) });
    },
});

// A `sectionId` moves the task to the bottom of that section.
export type TaskChanges = Partial<Pick<BoardTask, 'title' | 'description' | 'date' | 'isCompleted' | 'sectionId'>>;

export const updateTaskMutation = (queryClient: QueryClient, boardId: string) => ({
    mutationFn: ({ id, changes }: { id: string, changes: TaskChanges }) => updateSectionTaskQuery(id, changes),
    onSuccess: (_result: unknown, { id, changes }: { id: string, changes: TaskChanges }) => {
        // Read before the list is changed: where the task was decides which section it leaves.
        const movedFrom = changes.sectionId
            ? queryClient.getQueryData<BoardTask[]>(queryKeys.tasks(boardId))?.find((task) => task.id === id)?.sectionId
            : undefined;

        queryClient.setQueryData<BoardTask[]>(queryKeys.tasks(boardId), (tasks) => tasks && changeInList(tasks, id, changes));
        if (changes.sectionId) {
            queryClient.setQueryData<BoardDoc>(queryKeys.board(boardId), (board) => moveBetweenBoardSections(board, id, movedFrom, changes.sectionId!));
            queryClient.invalidateQueries({ queryKey: queryKeys.taskSearch(boardId) });
            // Without the list there is no knowing which section it left, so read the board's sections again.
            if (!movedFrom) queryClient.invalidateQueries({ queryKey: queryKeys.board(boardId) });
        }

        if ('isCompleted' in changes) queryClient.invalidateQueries({ queryKey: queryKeys.boardStats(boardId) });
        if ('title' in changes) queryClient.invalidateQueries({ queryKey: queryKeys.taskSearch(boardId) });
    },
});

export const deleteTaskMutation = (queryClient: QueryClient, boardId: string) => ({
    mutationFn: ({ id, sectionId }: { id: string, sectionId: string }) => deleteSectionTaskQuery(boardId, sectionId, id),
    onSuccess: (_result: unknown, { id, sectionId }: { id: string, sectionId: string }) => {
        queryClient.setQueryData<BoardTask[]>(queryKeys.tasks(boardId), (tasks) => tasks && removeFromList(tasks, id));
        queryClient.setQueryData<BoardDoc>(queryKeys.board(boardId), (board) => removeFromBoardSection(board, sectionId, id));
        // It may have been archived (deleted from the Archive tab), and it counted towards what came due.
        queryClient.setQueryData<ArchivedTask[]>(queryKeys.archivedTasks(boardId), (tasks) => tasks?.filter((task) => task.id !== id));
        queryClient.invalidateQueries({ queryKey: queryKeys.boardStats(boardId) });
        queryClient.invalidateQueries({ queryKey: queryKeys.taskSearch(boardId) });
    },
});

export const archiveTaskMutation = (queryClient: QueryClient, boardId: string) => ({
    mutationFn: (id: string) => setTaskArchivedQuery(id, true),
    onSuccess: (_result: unknown, id: string) => {
        // Archived tasks leave every list; they show up in the Archive tab instead.
        queryClient.setQueryData<BoardTask[]>(queryKeys.tasks(boardId), (tasks) => tasks && removeFromList(tasks, id));
        queryClient.invalidateQueries({ queryKey: queryKeys.archivedTasks(boardId) });
        queryClient.invalidateQueries({ queryKey: queryKeys.taskSearch(boardId) });
    },
});

export const restoreTaskMutation = (queryClient: QueryClient, boardId: string) => ({
    mutationFn: (id: string) => setTaskArchivedQuery(id, false),
    onSuccess: (_result: unknown, id: string) => {
        queryClient.setQueryData<ArchivedTask[]>(queryKeys.archivedTasks(boardId), (tasks) => tasks?.filter((task) => task.id !== id));
        // The restored task isn't in the list any more, so read it back from the server.
        queryClient.invalidateQueries({ queryKey: queryKeys.tasks(boardId) });
        queryClient.invalidateQueries({ queryKey: queryKeys.taskSearch(boardId) });
    },
});

// After something changed many tasks at once (a recurring series edited or deleted, tasks created in bulk): re-read
// everything that is made of tasks.
export const refreshBoardTasks = (queryClient: QueryClient, boardId: string) =>
    Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.tasks(boardId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.board(boardId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.archivedTasks(boardId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.boardStats(boardId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.taskSearch(boardId) }),
    ]);

export const useCreateTask = (boardId: string) => useMutation(createTaskMutation(useQueryClient(), boardId));
export const useUpdateTask = (boardId: string) => useMutation(updateTaskMutation(useQueryClient(), boardId));
export const useDeleteTask = (boardId: string) => useMutation(deleteTaskMutation(useQueryClient(), boardId));
export const useArchiveTask = (boardId: string) => useMutation(archiveTaskMutation(useQueryClient(), boardId));
export const useRestoreTask = (boardId: string) => useMutation(restoreTaskMutation(useQueryClient(), boardId));

export function useRefreshBoardTasks(boardId: string) {
    const queryClient = useQueryClient();
    return () => refreshBoardTasks(queryClient, boardId);
}
